import React, { useCallback, useEffect, useState } from 'react';
import { topographyApi } from '../lib/api';
import { topographySummary } from '../lib/topography-summary';

const FORMAT_LABELS = {
  stg: 'AGI SuperSting .stg',
  cmd: 'AGI command file',
  res2dinv: 'RES2DINV .dat',
  abem_ls: 'ABEM Terrameter LS',
  iris_prosys: 'IRIS Syscal / ProSys',
  pasi_gpd: 'PASI GPD',
  trn: 'Topography (.trn)',
  gimli_unified: 'pyGIMLi unified',
};

const fmtOhm = (v) => (v >= 1000 ? `${(v / 1000).toFixed(2)} kOhm` : `${Number(v).toPrecision(3)} ohm`);

/**
 * Electrode contact-resistance block (datasets.metadata.contact_resistance, from
 * backend/qc/contact_resistance.py). `source` is "measured" (ohms, judged against the kOhm
 * target) or "proxy" (median |V/I|, outliers only: never presented as a measurement).
 */
function ContactResistance({ cr }) {
  if (!cr || !cr.n_electrodes) return null;
  const proxy = cr.source === 'proxy';
  const worst = Array.isArray(cr.worst) ? cr.worst : [];
  return (
    <div className="mt-4 text-sm" data-testid="survey-contact">
      <div className="font-medium text-muted-foreground mb-1">
        {proxy ? 'Contact resistance (proxy)' : 'Contact resistance'}
      </div>
      <div className="font-semibold" data-testid="survey-contact-summary">
        {cr.n_over_limit} of {cr.n_electrodes} electrodes {proxy ? 'outlying' : `above ${cr.limit_kohm} kOhm`}
      </div>
      {worst.length > 0 && (
        <div className="text-xs text-muted-foreground mt-1" data-testid="survey-contact-worst">
          Worst: {worst.map((w) => `#${w.electrode} ${fmtOhm(w.ohms)}`).join(', ')}
        </div>
      )}
      {proxy && (
        <div className="text-xs text-muted-foreground mt-1">
          Median |V/I| per electrode (transfer resistance). The file has no contact-resistance
          measurement, so only electrodes that stand out from the rest are flagged.
        </div>
      )}
    </div>
  );
}

/**
 * Summary of a parsed raw resistivity/IP file (datasets.metadata written by the
 * backend's raw-format readers). Renders nothing until `metadata.format` exists.
 * `onShowOnMap` opens the map with the electrodes and pseudosection layers; `onOpenInGlobe`
 * (passed only when the globe feature is on) sends the dataset to the 3D globe.
 */
function SurveyCard({ metadata, onReparse, reparsing = false, onShowOnMap, onShowPseudosection, onTopography, datasetId, topoVersion = 0, onOpenInGlobe, openingGlobe = false }) {
  const [topo, setTopo] = useState(null);
  const showTopo = Boolean(onTopography && datasetId && metadata?.n_electrodes > 0);
  const loadTopo = useCallback(() => {
    topographyApi.get(datasetId)
      .then((res) => setTopo(res.data?.topography || null))
      .catch(() => setTopo(null));
  }, [datasetId]);
  // topoVersion is bumped by the page after the panel applies, to refetch.
  useEffect(() => { if (showTopo) loadTopo(); }, [showTopo, loadTopo, topoVersion]);
  const clearTopo = async () => {
    if (!window.confirm('Remove the stored topography from this survey?')) return;
    try { await topographyApi.clear(datasetId); } catch (e) { /* the refetch below shows the truth */ }
    loadTopo();
  };
  if (!metadata || !metadata.format) return null;
  const warnings = Array.isArray(metadata.warnings) ? metadata.warnings : [];
  const rows = [
    ['Format', FORMAT_LABELS[metadata.format] || metadata.format],
    ['Instrument', metadata.instrument || 'Unknown'],
    ['Electrodes', metadata.n_electrodes],
    ['Readings', metadata.n_readings],
    ['Array type', metadata.array_type || 'Unknown'],
    ['Spacing', metadata.spacing != null ? `${metadata.spacing} ${metadata.units || ''}`.trim() : 'Unknown'],
    ['IP windows', metadata.has_ip ? metadata.n_ip_windows : 'None'],
  ];
  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-sm" data-testid="survey-card">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Survey</h2>
        <div className="flex gap-2">
        {onShowPseudosection && (
          <button
            onClick={onShowPseudosection}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted"
            data-testid="survey-pseudosection"
          >
            Pseudosection
          </button>
        )}
        {onTopography && metadata.n_electrodes > 0 && (
          <button
            onClick={onTopography}
            disabled={!metadata.georef || metadata.georef.crs === 'local'}
            title={!metadata.georef || metadata.georef.crs === 'local' ? 'Georeference the survey first' : 'Sample the project DEM along the line'}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="topography-button"
          >
            Topography from DEM…
          </button>
        )}
        {onShowOnMap && (
          <button
            onClick={onShowOnMap}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted"
            data-testid="survey-show-on-map"
          >
            Show on map
          </button>
        )}
        {onOpenInGlobe && (
          <button
            onClick={onOpenInGlobe}
            disabled={openingGlobe}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="survey-open-in-globe"
          >
            {openingGlobe ? 'Opening...' : 'Open in Globe'}
          </button>
        )}
        {onReparse && (
          <button
            onClick={onReparse}
            disabled={reparsing}
            className="px-3 py-1 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="survey-reparse"
          >
            {reparsing ? 'Parsing...' : 'Re-parse file'}
          </button>
        )}
        </div>
      </div>
      {showTopo && topo && (
        <div className="mb-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="topography-status">
          <span>{topographySummary(topo)}</span>
          <button onClick={clearTopo} className="underline hover:text-foreground" data-testid="topography-clear">Remove</button>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-4">
        {rows.map(([label, value]) => (
          <div key={label}>
            <dt className="text-sm font-medium text-muted-foreground mb-1">{label}</dt>
            <dd className="text-sm font-semibold">{value}</dd>
          </div>
        ))}
      </dl>
      <ContactResistance cr={metadata.contact_resistance} />
      {warnings.length > 0 && (
        <ul className="mt-4 text-xs text-yellow-800 bg-yellow-100 rounded p-3 list-disc list-inside" data-testid="survey-warnings">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      <p className="mt-4 text-xs text-muted-foreground">
        {metadata.georef
          ? `Georeferenced (${metadata.georef.crs}). Show on map adds the electrodes and the pseudosection as layers.`
          : 'Electrode positions are in local survey coordinates. Georeference them to place the survey on the map.'}
      </p>
    </div>
  );
}

export default SurveyCard;
